import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDrawerFilter } from '@hapvida/hapvida-core-components';

import MovimentationService, {
  MovementListProps,
  MovementsSummaryProps,
} from '@services/MovimentationService';

import { formatMappedFilters, toastifyApiErrors } from '@utils';
import { useFilterParams, useAuth } from '@hooks';
import { ITEMS_LIMIT_PER_PAGE } from '@constants';
import { loadParamsBasedOnKeys } from 'hooks/services/beneficiaries/list/utils';
import { FilterParamsEnum } from 'constants/private/global/filterParamsEnum';

import {
  movementListFilterLabels,
  movementListFilterValues,
} from '../../../../constants/filterValues';

export function useMovementsMobileState() {
  const { searchParam, mappedFilters } = useFilterParams();
  const { getInitialFilter, setActiveFilters } = useDrawerFilter();
  const { enqueueSnackbar } = useSnackbar();
  const initialRender = useRef(true);
  const { selectedCompany, selectedPartnerCompany } = useAuth();
  const [searchParams] = useSearchParams();

  const movementService = useMemo(() => new MovimentationService(), []);

  const companyId = selectedCompany?.id ?? '';
  const partnerCompany = selectedPartnerCompany?.partnerCompany ?? '';

  const isEnabled = Boolean(companyId) && Boolean(partnerCompany);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      const initialFinancialFilters = getInitialFilter({
        filtersConfig: movementListFilterValues,
        filtersLabel: movementListFilterLabels,
      });

      setActiveFilters(initialFinancialFilters);
    }
  }, []);

  const mappedParams = loadParamsBasedOnKeys(searchParams, [
    FilterParamsEnum.STATUS,
    FilterParamsEnum.ORGANIZATIONAL_GROUP,
    FilterParamsEnum.START_DATE,
    FilterParamsEnum.END_DATE,
  ]);

  const formattedMappedFilters = useMemo(
    () => formatMappedFilters(mappedFilters),
    [mappedFilters],
  );

  const movementsMobileState = useInfiniteQuery<MovementListProps>({
    queryKey: [
      'movementsState',
      formattedMappedFilters,
      searchParam,
      companyId,
      partnerCompany,
    ],
    queryFn: ({ pageParam = 1 }) =>
      movementService.fetchMovements({
        filters: {
          ...formattedMappedFilters,
          ...searchParam,
          offset: ITEMS_LIMIT_PER_PAGE * (pageParam - 1),
          limit: ITEMS_LIMIT_PER_PAGE,
        },
        companyId,
      }),
    enabled: isEnabled,
    getNextPageParam: (lastPage, allPages) => {
      const hasNextPage =
        lastPage.total > allPages.length * ITEMS_LIMIT_PER_PAGE;

      return hasNextPage ? allPages.length + 1 : undefined;
    },
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const movementsSummaryState = useQuery<MovementsSummaryProps[]>({
    queryKey: [
      'movementsSummaryState',
      formattedMappedFilters,
      searchParam,
      companyId,
      partnerCompany,
      mappedParams,
    ],
    queryFn: () =>
      movementService.fetchMovementsSummary({
        companyId,
        ...formattedMappedFilters,
        ...searchParam,
        ...mappedParams,
      }),
    enabled: isEnabled,
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const refreshMovementsStates = () => {
    movementsMobileState.remove();
    movementsMobileState.refetch();
    movementsSummaryState.remove();
    movementsSummaryState.refetch();
  };

  return {
    refreshMovementsStates,
    movementsSummaryState,
    movementsMobileState,
  };
}
